import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { VetaCenterConnection, VetaCenterProjectLink } from '~hooks/useVetaCenter/models';
import useVetaCenter from '~hooks/useVetaCenter/useVetaCenter';
import { getVetaCenterProjectLinksQueryKey } from '~queries/useVetaCenterProjectLinksQuery';
import { getVetaCenterReadinessItemsQueryKey } from '~queries/vetaCenterReadinessItemsQueryKey';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import useSingleFlight from '~utils/hooks/useSingleFlight';

import readVetaCenterRejection from './readVetaCenterRejection';
import VetaCenterAddProjectLinkForm, {
  AddProjectLinkDto,
} from './VetaCenterAddProjectLinkForm';

interface VetaCenterProjectLinksProps {
  companyId: string;
  hasProjectLinksError: boolean;
  isLoadingProjectLinks: boolean;
  projectLinks: VetaCenterProjectLink[];
  vetaCenterConnection: VetaCenterConnection;
}

export default function VetaCenterProjectLinks({
  companyId,
  hasProjectLinksError,
  isLoadingProjectLinks,
  projectLinks,
  vetaCenterConnection,
}: VetaCenterProjectLinksProps) {
  const { toasterStore } = useStores();
  const queryClient = useQueryClient();
  const { createProjectLink, deleteProjectLink } = useVetaCenter();
  const [linkToRemove, setLinkToRemove] = useState<VetaCenterProjectLink | null>(null);
  const { isInFlight: isMutating, runSingleFlight } = useSingleFlight();

  const workspaces = vetaCenterConnection.contracts.flatMap((contract) =>
    contract.workspaces.map((workspace) => ({
      contract,
      key: `${contract.contractGuid}/${workspace.workspaceGuid}`,
      label: `${contract.contractId ?? contract.contractGuid} · ${workspace.workspaceName ?? workspace.workspaceGuid}`,
      workspace,
    })),
  );

  const linkedProjectIds = projectLinks.map((link) => link.project.id);

  const refreshLinks = () => {
    void queryClient.invalidateQueries({
      queryKey: getVetaCenterReadinessItemsQueryKey(companyId),
    });

    return queryClient.invalidateQueries({
      queryKey: getVetaCenterProjectLinksQueryKey(companyId),
    });
  };

  const handleAdd = async (formData: AddProjectLinkDto) => {
    const selected = workspaces.find(
      (candidate) => candidate.key === formData.workspaceKey,
    );
    if (!selected || !formData.project) return false;
    const { project } = formData;

    const wasAdded = await runSingleFlight(async () => {
      try {
        const created = await createProjectLink(companyId, {
          externalContractGuid: selected.contract.contractGuid,
          externalWorkspaceGuid: selected.workspace.workspaceGuid,
          projectId: project.value,
        });
        if (!created) return false;
      } catch (error) {
        toasterStore.push(
          alert(
            readVetaCenterRejection(error).message ??
              t('error_messages.veta_center.failed_to_create_project_link'),
            AlertTypes.error,
          ),
        );

        return false;
      }

      await refreshLinks();
      toasterStore.push(
        alert(
          t('administration.integration.veta_center.toast.project_linked'),
          AlertTypes.success,
        ),
      );

      return true;
    });

    return wasAdded === true;
  };

  const handleRemoveClose = () => {
    if (isMutating) return;

    setLinkToRemove(null);
  };

  const handleRemoveConfirm = () =>
    runSingleFlight(async () => {
      if (!linkToRemove) return;

      const deleted = await deleteProjectLink(companyId, linkToRemove.id);
      if (!deleted) return;

      await refreshLinks();
      setLinkToRemove(null);
      toasterStore.push(
        alert(
          t('administration.integration.veta_center.toast.project_unlinked'),
          AlertTypes.success,
        ),
      );
    });

  const renderLink = (link: VetaCenterProjectLink) => (
    <Box
      key={link.id}
      sx={{
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        py: 1,
      }}
    >
      <Typography>
        {link.project.name}
        {link.project.externalId ? ` · ${link.project.externalId}` : ''}
      </Typography>
      <SmallButton
        color="brandV2Black"
        onClick={() => setLinkToRemove(link)}
        sx={{ color: 'text.primary' }}
        variant="text"
      >
        {t('actions.remove')}
      </SmallButton>
    </Box>
  );

  const orphanedLinks = projectLinks.filter(
    (link) =>
      !workspaces.some(
        ({ contract, workspace }) =>
          link.externalContractGuid === contract.contractGuid &&
          link.externalWorkspaceGuid === workspace.workspaceGuid,
      ),
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6">
        {t('administration.integration.veta_center.projects.title')}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: 14, mb: 2 }}>
        {t('administration.integration.veta_center.projects.no_backfill')}
      </Typography>

      {isLoadingProjectLinks ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : null}

      {hasProjectLinksError ? (
        <Alert severity="error">
          {t('error_messages.veta_center.failed_to_fetch_project_links')}
        </Alert>
      ) : null}

      {isLoadingProjectLinks || hasProjectLinksError ? null : (
        <>
          {workspaces.map(({ contract, key, label, workspace }) => {
            const linksInWorkspace = projectLinks.filter(
              (link) =>
                link.externalContractGuid === contract.contractGuid &&
                link.externalWorkspaceGuid === workspace.workspaceGuid,
            );

            return (
              <Box key={key} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                {linksInWorkspace.length === 0 ? (
                  <Typography sx={{ color: 'text.secondary' }}>
                    {t('administration.integration.veta_center.projects.none_linked')}
                  </Typography>
                ) : (
                  linksInWorkspace.map(renderLink)
                )}
              </Box>
            );
          })}

          {orphanedLinks.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>
                {t(
                  'administration.integration.veta_center.projects.workspace_unavailable',
                )}
              </Typography>
              {orphanedLinks.map(renderLink)}
            </Box>
          ) : null}

          {workspaces.length > 0 ? (
            <VetaCenterAddProjectLinkForm
              companyId={companyId}
              isMutating={isMutating}
              key={workspaces.map(({ key }) => key).join('|')}
              linkedProjectIds={linkedProjectIds}
              onAdd={handleAdd}
              workspaceOptions={workspaces}
            />
          ) : null}
        </>
      )}

      <ConfirmationDialog
        confirmButtonProps={{ children: t('actions.remove') }}
        isBusy={isMutating}
        isOpen={linkToRemove !== null}
        onClose={handleRemoveClose}
        onConfirm={handleRemoveConfirm}
        title={t(
          'administration.integration.veta_center.projects.remove_confirmation_title',
        )}
      >
        <Typography>
          {t('administration.integration.veta_center.projects.remove_confirmation_body', {
            interpolation: { escapeValue: false },
            project: linkToRemove?.project.name,
          })}
        </Typography>
      </ConfirmationDialog>
    </Box>
  );
}
